import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as Plus } from '../../../assets/icons/plus.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import timezones from '../../../enum/AllTimeZoneList';
import currencyCodesList from '../../../enum/CurrencyCodesList';
import {
  PriceComponentInterface,
  AddTariffPayloadInterface,
  ParkingFeeDetailsInterface,
  ReservationFeeDetailsInterface,
} from '../../../interface/tariff.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAccountAllList } from '../../../redux/slices/AccountManagementSlice';
import {
  fetchAllConnectorRating,
  fetchConnectorScopList,
} from '../../../redux/slices/ConnectorRatingSlice';
import {
  fetchAllTariffs as fetchAllCustomTariffs,
  fetchTariffCounts as fetchCustomTariffCounts,
} from '../../../redux/slices/CustomTariffSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllTariffs, fetchTariffCounts } from '../../../redux/slices/TariffSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomMultiDatePicker from '../../../shared-components/custom-date-range/CustomMultiDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTimePicker from '../../../shared-components/custom-time-input/CustomTimePicker';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import { changeDateFormat } from '../../../shared-functions/DataTime';
import { getMyAccountCustomId } from '../../../shared-functions/JWTDocodeFunction';
import { formatDateWithTimezone } from '../../../shared-functions/TimezoneConversions';
import penaltyManagementApi from '../../../utils/api/penalty-management';
import tariffApi from '../../../utils/api/tariff';
import { generate6DigitNumber } from '../../../utils/brandingUtils';

/**
 * Add-tariff component
 * @returns {any} Add-tariff component
 */
function AddTariff() {
  const { t } = useTranslation('tariff');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { portalType } = usePortal();
  const timeSlot = true;

  const daysOfWeekOptions = [
    { label: t('monday'), value: 'monday' },
    { label: t('tuesday'), value: 'tuesday' },
    { label: t('wednesday'), value: 'wednesday' },
    { label: t('thursday'), value: 'thursday' },
    { label: t('friday'), value: 'friday' },
    { label: t('saturday'), value: 'saturday' },
    { label: t('sunday'), value: 'sunday' },
  ];

  const { connectorRatingsAll, scopeList } = useAppSelector((state) => state.connectorRating);
  const { accountList } = useAppSelector((state) => state.accounts);
  const [loading, setLoading] = useState<boolean>(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [stationsList, setStationsList] = useState<Array<{ label: string; value: string }>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [priceComponentErrors, setPriceComponentErrors] = useState<
    Record<number, { name?: string; value?: string }>
  >({});
  const [timeSlotErrors, setTimeSlotErrors] = useState<
    Record<
      number,
      {
        name?: string;
        startTime?: string;
        endTime?: string;
        recurrenceType?: string;
        priceMultiplier?: string;
        dates?: string;
        daysOfWeek?: string;
      }
    >
  >({});
  const [parkingFeeErrors, setParkingFeeErrors] = useState<Record<string, string>>({});
  const [reservationFeeErrors, setReservationFeeErrors] = useState<Record<string, string>>({});
  const [taxComponentErrors, setTaxComponentErrors] = useState<
    Record<number, { name?: string; percentage?: string }>
  >({});

  const scopeLeveelOptions =
    portalType === PortalType.PARTNER
      ? [
          { label: t('station'), value: 'station' },
          { label: t('geography'), value: 'geography' },
        ]
      : [
          { label: t('partner'), value: 'partner' },
          { label: t('station'), value: 'station' },
          { label: t('geography'), value: 'geography' },
        ];

  const [tariffDetails, setTariffDetails] = useState<AddTariffPayloadInterface>({
    status: true,
    enforcementDate: '',
    currency: '',
    timezone: '',
    expiryDate: '',
    tariffName: '',
    tariffType: '',
    currentType: '',
    connectorRating: '',
    stateChildType: 'State',
    cityChildType: 'City',
    scope: '',
    station: '',
    priceComponents: [
      { id: 1, type: 'Base price', name: '', valueType: 'per-kWh', value: '', order: '0' },
      { id: 2, type: 'Operational cost', name: '', valueType: 'per-kWh', value: '', order: '1' },
    ],
    timeBasedPricingSlots: [],
    isParkingFeeEnabled: false,
    parkingFeeDetails: {
      price: '',
      unit: 'minute',
      graceMinutes: '',
      minChargingDurationMinutes: '',
      billingStepMinutes: '1',
      maxCap: '',
    },
    isReservationFeeEnabled: false,
    reservationFeeDetails: {
      reservationPrice: { priceType: 'per-minute', value: '' },
      graceMinutes: '',
      maxHoldMinutes: '',
      noShowCharge: { chargeType: 'held-time', value: '' },
      feeOnSuccess: { actionType: 'waive', value: '' },
      expiryGraceMinutes: '',
    },
    taxCountry: { scopeId: '', scopeName: '' },
    taxComponents: [],
  });

  /**
   * Get the scope name
   * @param {string} scope - Scope level (country, state, city)
   * @param {string} scopeId - Scope entity id
   * @returns {string} Scope display label or empty string
   */
  const getScopeName = (scope: string, scopeId: string) => {
    const currentScopeList: any[] = (scopeList as Record<string, any[]> | undefined)?.[scope] || [];
    return currentScopeList.find((scopeItem) => scopeItem.value === scopeId)?.label || '';
  };

  /**
   * Get the child type for a given scope and ID
   * @param {string} scope - The scope
   * @param {string} scopeId - The scope ID
   * @returns {string} The child type
   */
  const getChildType = (scope: string, scopeId: string) => {
    const currentScopeList: any[] = (scopeList as Record<string, any[]> | undefined)?.[scope] || [];
    return currentScopeList.find((scopeItem) => scopeItem.value === scopeId)?.childType || '';
  };
  /**
   * Get the partner and station name
   * @returns {string} The child type
   */
  const getPartnerStationName = (type: string, value: string): string => {
    let scopeName = '';
    if (type === 'PNR') {
      scopeName =
        accountList?.find((account) => account.accountCustomId === value)?.accountName || '';
    }
    if (type === 'STN') {
      scopeName = stationsList?.find((account) => String(account.value) === value)?.label || '';
    }
    return scopeName;
  };

  /**
   * Update tariff details field
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateTariffDetail = (
    field: string,
    value: string | number | boolean | PriceComponentInterface[] | null,
  ) => {
    const chargeData = {
      [field]: value,
    };

    if (errors[field]) {
      setErrors((previous) => ({ ...previous, [field]: '' }));
    }

    switch (field) {
      // case 'currentType':
      //   dispatch(fetchAllConnectorRating(String(value)));
      //   break;
      case 'tariffType':
        chargeData.scope = null;
        chargeData.state = null;
        chargeData.city = null;
        chargeData.country = null;
        chargeData.station = null;
        chargeData.partner = null;
        chargeData.scopeId = null;
        chargeData.scopeName = null;
        if (value === 'global' && portalType !== PortalType.PARTNER) {
          setErrors((previous) => ({ ...previous, expiryDate: '' }));
        }
        break;
      case 'scope':
        if (value === 'geography') {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
        } else if (value !== 'geography' && portalType !== PortalType.PARTNER) {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          dispatch(fetchAccountAllList());
        } else {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          const accountCustomId = getMyAccountCustomId();
          penaltyManagementApi.findStationNames(accountCustomId).then((response) => {
            setStationsList(
              response.data.map((station: any) => ({ label: station.name, value: station._id })) ||
                [],
            );
          });
        }
        break;
      case 'country':
        dispatch(fetchConnectorScopList({ scope: 'state', scopeId: value }));
        chargeData.state = null;
        chargeData.city = null;
        chargeData.stateChildType = getChildType('country', String(value));
        chargeData.scopeId = value;
        chargeData.scopeName = getScopeName('country', String(value));
        break;
      case 'state':
        dispatch(fetchConnectorScopList({ scope: 'city', scopeId: value }));
        chargeData.scopeId = value;
        chargeData.scopeName = getScopeName('state', String(value));
        chargeData.city = null;
        chargeData.cityChildType = getChildType('state', String(value));
        break;
      case 'city':
        chargeData.scopeId = value;
        chargeData.scopeName = getScopeName('city', String(value));
        break;
      case 'partner':
        chargeData.scopeId = generate6DigitNumber('PNR');
        chargeData.scopeName = getPartnerStationName('PNR', String(value));
        penaltyManagementApi.findStationNames(String(value)).then((response) => {
          setStationsList(
            response.data.map((station: any) => ({ label: station.name, value: station._id })) ||
              [],
          );
        });
        break;
      case 'station':
        chargeData.scopeId = generate6DigitNumber('STN');
        chargeData.scopeName = getPartnerStationName('STN', String(value));
        break;

      default:
        break;
    }

    setTariffDetails((previous) => ({ ...previous, ...chargeData }));
  };

  /**
   * Add price component to the price components list
   */
  const addPriceComponent = () => {
    updateTariffDetail('priceComponents', [
      ...tariffDetails.priceComponents,
      {
        id: Date.now(),
        type: '',
        name: '',
        valueType: 'per-kWh',
        value: '',
        order: String(tariffDetails.priceComponents.length),
      },
    ]);
  };
  /**
   * Check if a price component can be deleted.
   * Total length must be greater than 2, and deleting the component must leave
   * at least one Base Price component and at least one Operational Cost component.
   * @param {PriceComponentInterface} comp - The component to evaluate
   * @returns {boolean} True if deletion is allowed, false otherwise
   */
  const canDeletePriceComponent = (comp: PriceComponentInterface): boolean => {
    if (tariffDetails.priceComponents.length <= 2) return false;
    const typeString = comp.type ? comp.type.toLowerCase() : '';
    const isBase = typeString.includes('base');
    const isOps = typeString.includes('operat');

    if (isBase) {
      const baseCount = tariffDetails.priceComponents.filter((c) =>
        c.type ? c.type.toLowerCase().includes('base') : false,
      ).length;
      if (baseCount <= 1) return false;
    }
    if (isOps) {
      const opsCount = tariffDetails.priceComponents.filter((c) =>
        c.type ? c.type.toLowerCase().includes('operat') : false,
      ).length;
      if (opsCount <= 1) return false;
    }
    return true;
  };

  /**
   * Remove price component from the price components list
   * @param {number} id - Price component id
   * @returns {void}
   */
  const removePriceComponent = (id: number) => {
    const compTarget = tariffDetails.priceComponents.find((comp) => comp.id === id);
    if (!compTarget || !canDeletePriceComponent(compTarget)) return;
    updateTariffDetail(
      'priceComponents',
      tariffDetails.priceComponents.filter((comp) => comp.id !== id),
    );
  };

  /**
   * Update a specific field for a price component
   * @param {number} id - Price component id
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updatePriceComponent = (id: number, field: string, value: string | number) => {
    if ((field === 'name' || field === 'value') && priceComponentErrors[id]?.[field]) {
      setPriceComponentErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], [field]: '' },
      }));
    }
    if (field === 'type' && errors.priceComponents) {
      setErrors((previous) => ({ ...previous, priceComponents: '' }));
    }
    updateTariffDetail(
      'priceComponents',
      tariffDetails.priceComponents.map((comp) =>
        comp.id === id ? { ...comp, [field]: value } : comp,
      ),
    );
  };

  /**
   * Add a new empty time-based pricing slot
   */
  const addTimeSlot = () => {
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: [
        ...(previous.timeBasedPricingSlots || []),
        {
          id: Date.now(),
          name: '',
          startTime: '',
          endTime: '',
          recurrenceType: '',
          daysOfWeek: [],
          priceMultiplier: '1',
          dates: [],
        },
      ],
    }));
  };

  /**
   * Remove a time-based pricing slot by id
   * @param {number} id - Slot id to remove
   */
  const removeTimeSlot = (id: number) => {
    setTimeSlotErrors((previous) => {
      const updated = { ...previous };
      delete updated[id];
      return updated;
    });
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: (previous.timeBasedPricingSlots || []).filter(
        (slot) => slot.id !== id,
      ),
    }));
  };

  /**
   * Update a field on a time-based pricing slot
   * @param {number} id - Slot id
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateTimeSlot = (id: number, field: string, value: string | string[]) => {
    if (timeSlotErrors[id]?.[field as keyof (typeof timeSlotErrors)[number]]) {
      setTimeSlotErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], [field]: '' },
      }));
    }
    if (field === 'recurrenceType' && timeSlotErrors[id]) {
      setTimeSlotErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], recurrenceType: '', dates: '', daysOfWeek: '' },
      }));
    }
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: (previous.timeBasedPricingSlots || []).map((slot) => {
        if (slot.id !== id) {
          return slot;
        }
        if (field === 'recurrenceType') {
          return {
            ...slot,
            recurrenceType: value as string,
            dates: value === 'non-recurring' ? slot.dates || [] : [],
            daysOfWeek: value === 'weekly' ? slot.daysOfWeek || [] : [],
          };
        }
        return { ...slot, [field]: value };
      }),
    }));
  };

  /**
   * Update a field on the parking fee details block
   * @param {string} field - Field name to update
   * @param {string} value - New value
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const updateParkingFeeDetail = (field: string, value: string) => {
    if (parkingFeeErrors[field]) {
      setParkingFeeErrors((previous) => ({ ...previous, [field]: '' }));
    }
    setTariffDetails((previous) => ({
      ...previous,
      parkingFeeDetails: {
        ...(previous.parkingFeeDetails as ParkingFeeDetailsInterface),
        [field]: value,
      },
    }));
  };

  /**
   * Update a field on the reservation fee details block. Field accepts a
   * dot-path (e.g. "reservationPrice.priceType") for nested sub-objects.
   * @param {string} field - Field name (or "group.field") to update
   * @param {string} value - New value
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const updateReservationFeeDetail = (field: string, value: string) => {
    if (reservationFeeErrors[field]) {
      setReservationFeeErrors((previous) => ({ ...previous, [field]: '' }));
    }
    setTariffDetails((previous) => {
      const current = previous.reservationFeeDetails as ReservationFeeDetailsInterface;
      if (field.includes('.')) {
        const [group, key] = field.split('.');
        return {
          ...previous,
          reservationFeeDetails: {
            ...current,
            [group]: { ...(current as any)[group], [key]: value },
          },
        };
      }
      return {
        ...previous,
        reservationFeeDetails: { ...current, [field]: value },
      };
    });
  };

  /**
   * Update the tax country (jurisdiction) for this tariff
   * @param {string} scopeId - Selected country scope id
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const updateTaxCountry = (scopeId: string) => {
    if (errors.taxCountry) {
      setErrors((previous) => ({ ...previous, taxCountry: '' }));
    }
    const countryOptions =
      (scopeList as { country?: Array<{ label: string; value: string }> })?.country || [];
    const scopeName = countryOptions.find((option) => option.value === scopeId)?.label || '';
    setTariffDetails((previous) => ({
      ...previous,
      taxCountry: { scopeId, scopeName },
    }));
  };

  /**
   * Add a new empty tax component
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const addTaxComponent = () => {
    setTariffDetails((previous) => ({
      ...previous,
      taxComponents: [
        ...(previous.taxComponents || []),
        { id: Date.now(), name: '', percentage: '' },
      ],
    }));
  };

  /**
   * Update a specific field for a tax component
   * @param {number} id - Tax component id
   * @param {string} field - Field name to update
   * @param {string} value - New value
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const updateTaxComponent = (id: number, field: string, value: string) => {
    if (taxComponentErrors[id]?.[field as 'name' | 'percentage']) {
      setTaxComponentErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], [field]: '' },
      }));
    }
    setTariffDetails((previous) => ({
      ...previous,
      taxComponents: (previous.taxComponents || []).map((comp) =>
        comp.id === id ? { ...comp, [field]: value } : comp,
      ),
    }));
  };

  /**
   * Remove a tax component by id
   * @param {number} id - Tax component id to remove
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const removeTaxComponent = (id: number) => {
    setTaxComponentErrors((previous) => {
      const updated = { ...previous };
      delete updated[id];
      return updated;
    });
    setTariffDetails((previous) => ({
      ...previous,
      taxComponents: (previous.taxComponents || []).filter((comp) => comp.id !== id),
    }));
  };

  /**
   * Validate required tariff fields and populate inline field errors.
   * @returns {boolean} True when the form is valid, false otherwise
   */
  const validateTariffDetails = (): boolean => {
    const newErrors: Record<string, string> = {};
    const newPriceComponentErrors: Record<number, { name?: string; value?: string }> = {};
    const newTimeSlotErrors: Record<
      number,
      {
        name?: string;
        startTime?: string;
        endTime?: string;
        recurrenceType?: string;
        priceMultiplier?: string;
        dates?: string;
        daysOfWeek?: string;
      }
    > = {};

    if (!tariffDetails.tariffName) {
      newErrors.tariffName = t('tariffNameRequired');
    }
    if (!tariffDetails.currency) {
      newErrors.currency = t('currencyCodeRequired');
    }
    if (!tariffDetails.timezone) {
      newErrors.timezone = t('timeZoneRequired');
    }
    if (!tariffDetails.tariffType) {
      newErrors.tariffType = t('tariffTypeRequired');
    }
    if (tariffDetails.tariffType === 'custom') {
      if (
        tariffDetails.scope === 'partner' &&
        portalType !== PortalType.PARTNER &&
        !tariffDetails.partner
      ) {
        newErrors.partner = t('partnerRequired');
      }
      if (tariffDetails.scope === 'station') {
        if (portalType !== PortalType.PARTNER && !tariffDetails.partner) {
          newErrors.partner = t('partnerRequired');
        }
        if (!tariffDetails.station) {
          newErrors.station = t('stationRequired');
        }
      }
      if (tariffDetails.scope === 'geography' && !tariffDetails.country) {
        newErrors.country = t('countryRequired');
      }
      if (!tariffDetails.scope) {
        newErrors.scope = t('scopeRequired');
      }
    }
    if (!tariffDetails.currentType) {
      newErrors.currentType = t('currentTypeRequired');
    }
    if (!tariffDetails.connectorRating) {
      newErrors.connectorRating = t('connectorRatingRequired');
    }
    if (!tariffDetails.enforcementDate) {
      newErrors.enforcementDate = t('enforcementDateRequired');
    }

    // Validate price components
    const hasBasePrice = tariffDetails.priceComponents.some(
      (comp) => comp.type && comp.type.toLowerCase().includes('base'),
    );
    const hasOperationalCost = tariffDetails.priceComponents.some(
      (comp) => comp.type && comp.type.toLowerCase().includes('operat'),
    );

    if (!hasBasePrice || !hasOperationalCost) {
      newErrors.priceComponents = t('priceComponentsRequired');
    }

    tariffDetails.priceComponents.forEach((comp) => {
      if (!comp.name) {
        newPriceComponentErrors[comp.id] = {
          ...newPriceComponentErrors[comp.id],
          name: t('componentNameRequired'),
        };
      }
      if (!comp.value) {
        newPriceComponentErrors[comp.id] = {
          ...newPriceComponentErrors[comp.id],
          value: t('valueRequired'),
        };
      }
    });

    // Validate time-based pricing slots if any exist
    if (tariffDetails.timeBasedPricingSlots && tariffDetails.timeBasedPricingSlots.length > 0) {
      tariffDetails.timeBasedPricingSlots.forEach((slot) => {
        const slotError: {
          name?: string;
          startTime?: string;
          endTime?: string;
          recurrenceType?: string;
          priceMultiplier?: string;
          dates?: string;
          daysOfWeek?: string;
        } = {};

        if (!slot.name || !slot.name.trim()) {
          slotError.name = t('slotNameRequired');
        }
        if (!slot.startTime) {
          slotError.startTime = t('startTimeRequired');
        }
        if (!slot.endTime) {
          slotError.endTime = t('endTimeRequired');
        } else if (slot.startTime && slot.startTime >= slot.endTime) {
          slotError.endTime = t('endTimeAfterStartTime');
        }
        if (!slot.recurrenceType) {
          slotError.recurrenceType = t('recurrenceTypeRequired');
        }
        if (!slot.priceMultiplier || String(slot.priceMultiplier).trim() === '') {
          slotError.priceMultiplier = t('priceMultiplierRequired');
        }
        if (slot.recurrenceType === 'non-recurring' && (!slot.dates || slot.dates.length === 0)) {
          slotError.dates = t('dateRequired');
        }
        if (
          slot.recurrenceType === 'weekly' &&
          (!slot.daysOfWeek || slot.daysOfWeek.length === 0)
        ) {
          slotError.daysOfWeek = t('daysOfWeekRequired');
        }

        if (Object.keys(slotError).length > 0) {
          newTimeSlotErrors[slot.id] = slotError;
        }
      });
    }

    // Validate parking fee details when enabled
    const newParkingFeeErrors: Record<string, string> = {};
    if (tariffDetails.isParkingFeeEnabled) {
      const parking = tariffDetails.parkingFeeDetails;
      if (parking?.price === '' || parking?.price === undefined || parking?.price === null) {
        newParkingFeeErrors.price = t('parkingPriceRequired');
      }
      if (
        parking?.graceMinutes === '' ||
        parking?.graceMinutes === undefined ||
        parking?.graceMinutes === null
      ) {
        newParkingFeeErrors.graceMinutes = t('graceMinutesRequired');
      }
      if (!parking?.billingStepMinutes || Number(parking.billingStepMinutes) <= 0) {
        newParkingFeeErrors.billingStepMinutes = t('billingStepRequired');
      }
      if (
        parking?.minChargingDurationMinutes !== '' &&
        parking?.minChargingDurationMinutes !== undefined &&
        parking?.minChargingDurationMinutes !== null &&
        Number(parking.minChargingDurationMinutes) < 0
      ) {
        newParkingFeeErrors.minChargingDurationMinutes = t('minChargingDurationMustBeValid');
      }
      if (parking?.maxCap && Number(parking.maxCap) <= 0) {
        newParkingFeeErrors.maxCap = t('maxCapMustBePositive');
      }
    }

    // Validate reservation fee details when enabled
    const newReservationFeeErrors: Record<string, string> = {};
    if (tariffDetails.isReservationFeeEnabled) {
      const reservation = tariffDetails.reservationFeeDetails;
      if (!reservation?.reservationPrice?.priceType) {
        newReservationFeeErrors['reservationPrice.priceType'] = t('reservationPriceTypeRequired');
      } else if (
        reservation.reservationPrice.priceType !== 'zero' &&
        (!reservation.reservationPrice.value || Number(reservation.reservationPrice.value) <= 0)
      ) {
        newReservationFeeErrors['reservationPrice.value'] = t('reservationPriceValueRequired');
      }
      if (
        reservation?.maxHoldMinutes === '' ||
        reservation?.maxHoldMinutes === undefined ||
        reservation?.maxHoldMinutes === null
      ) {
        newReservationFeeErrors.maxHoldMinutes = t('maxHoldMinutesRequired');
      }
      if (
        reservation?.graceMinutes === '' ||
        reservation?.graceMinutes === undefined ||
        reservation?.graceMinutes === null
      ) {
        newReservationFeeErrors.graceMinutes = t('graceMinutesRequired');
      } else if (
        reservation?.maxHoldMinutes &&
        Number(reservation.graceMinutes) > Number(reservation.maxHoldMinutes)
      ) {
        newReservationFeeErrors.graceMinutes = t('graceCannotExceedMaxHold');
      }
      if (!reservation?.noShowCharge?.chargeType) {
        newReservationFeeErrors['noShowCharge.chargeType'] = t('noShowChargeTypeRequired');
      } else if (
        reservation.noShowCharge.chargeType === 'flat' &&
        (!reservation.noShowCharge.value || Number(reservation.noShowCharge.value) <= 0)
      ) {
        newReservationFeeErrors['noShowCharge.value'] = t('noShowChargeValueRequired');
      }
      if (!reservation?.feeOnSuccess?.actionType) {
        newReservationFeeErrors['feeOnSuccess.actionType'] = t('feeOnSuccessActionRequired');
      }
      if (
        reservation?.expiryGraceMinutes === '' ||
        reservation?.expiryGraceMinutes === undefined ||
        reservation?.expiryGraceMinutes === null
      ) {
        newReservationFeeErrors.expiryGraceMinutes = t('expiryGraceMinutesRequired');
      }
    }

    // Validate tax components, if any are configured
    const newTaxComponentErrors: Record<number, { name?: string; percentage?: string }> = {};
    (tariffDetails.taxComponents || []).forEach((comp) => {
      if (!comp.name || !comp.name.trim()) {
        newTaxComponentErrors[comp.id] = {
          ...newTaxComponentErrors[comp.id],
          name: t('taxComponentNameRequired'),
        };
      }
      if (
        comp.percentage === '' ||
        comp.percentage === undefined ||
        comp.percentage === null ||
        Number(comp.percentage) < 0 ||
        Number(comp.percentage) > 100
      ) {
        newTaxComponentErrors[comp.id] = {
          ...newTaxComponentErrors[comp.id],
          percentage: t('taxPercentageInvalid'),
        };
      }
    });

    setErrors(newErrors);
    setPriceComponentErrors(newPriceComponentErrors);
    setTimeSlotErrors(newTimeSlotErrors);
    setParkingFeeErrors(newParkingFeeErrors);
    setReservationFeeErrors(newReservationFeeErrors);
    setTaxComponentErrors(newTaxComponentErrors);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newPriceComponentErrors).length === 0 &&
      Object.keys(newTimeSlotErrors).length === 0 &&
      Object.keys(newParkingFeeErrors).length === 0 &&
      Object.keys(newReservationFeeErrors).length === 0 &&
      Object.keys(newTaxComponentErrors).length === 0
    );
  };

  /**
   * Handle save tariff
   */
  const handleSave = () => {
    if (!validateTariffDetails()) {
      return;
    }

    const scopeLevels = [];
    if (tariffDetails.country) {
      scopeLevels.push({
        scope: 'country',
        scopeId: tariffDetails.country,
        scopeName: getScopeName('country', tariffDetails.country),
      });
    }
    if (tariffDetails.state) {
      scopeLevels.push({
        scope: 'state',
        scopeId: tariffDetails.state,
        scopeName: getScopeName('state', tariffDetails.state),
      });
    }
    if (tariffDetails.city) {
      scopeLevels.push({
        scope: 'city',
        scopeId: tariffDetails.city,
        scopeName: getScopeName('city', tariffDetails.city),
      });
    }

    const request: any = {
      name: tariffDetails.tariffName,
      currency: tariffDetails.currency,
      type: tariffDetails.tariffType,
      currentType: tariffDetails.currentType,
      timezone: tariffDetails.timezone,
      effectiveFrom: tariffDetails.enforcementDate
        ? formatDateWithTimezone(tariffDetails.enforcementDate, tariffDetails.timezone)
        : null,
      isActive: tariffDetails.status,
      tariffPricingComponent: tariffDetails.priceComponents.map((price: any) => ({
        componentName: price.name,
        componentType: price.type,
        valueType: price.valueType,
        value: price.value,
        order: price.order,
      })),
    };

    if (
      tariffDetails.expiryDate &&
      formatDateWithTimezone(tariffDetails.expiryDate, tariffDetails.timezone)
    ) {
      request.effectiveTo = formatDateWithTimezone(
        tariffDetails.expiryDate,
        tariffDetails.timezone,
      );
    }

    if (tariffDetails.connectorRating) {
      const connectorRating: any = connectorRatingsAll.find(
        (rating: any) => rating._id === tariffDetails.connectorRating,
      );

      request.connectorRating = connectorRating
        ? {
            ratingId: connectorRating._id,
            name: connectorRating.name,
            capacityKW: connectorRating.capacityKW,
            currentType: connectorRating.currentType,
            unit: connectorRating.unit,
          }
        : null;
    }

    if (tariffDetails.scope) {
      request.scope = tariffDetails.scope;
    }
    if (tariffDetails.scopeId) {
      request.scopeId = tariffDetails.scopeId;
    }
    if (tariffDetails.scopeName) {
      request.scopeName = tariffDetails.scopeName;
    }

    if (tariffDetails.tariffType === 'custom' && tariffDetails.partner) {
      request.accountCustomId = tariffDetails.partner;
    }
    if (portalType === PortalType.PARTNER) {
      request.accountCustomId = getMyAccountCustomId();
    }
    if (tariffDetails.tariffType === 'custom' && tariffDetails.station) {
      request.stationId = tariffDetails.station;
    }

    if (request.scope === 'geography' && scopeLevels.length > 0) {
      request.scopeLevels = scopeLevels;
    }

    if (tariffDetails.timeBasedPricingSlots && tariffDetails.timeBasedPricingSlots.length > 0) {
      request.isTimeBasedPricingEnabled = true;
      request.timeBasedPricingDetails = tariffDetails.timeBasedPricingSlots.map((slot) => {
        const slotDetail: {
          name: string;
          startTime: string;
          endTime: string;
          recurrenceType: string;
          priceMultiplier: number;
          daysOfWeek?: string[];
          dates?: string[];
        } = {
          name: slot.name,
          startTime: slot.startTime,
          endTime: slot.endTime,
          recurrenceType: slot.recurrenceType,
          priceMultiplier: Number(slot.priceMultiplier),
        };

        if (slot.recurrenceType === 'weekly' && slot.daysOfWeek && slot.daysOfWeek.length > 0) {
          slotDetail.daysOfWeek = slot.daysOfWeek;
        } else if (
          slot.recurrenceType === 'non-recurring' &&
          Array.isArray(slot.dates) &&
          slot.dates.length > 0
        ) {
          slotDetail.dates = slot.dates;
        }

        return slotDetail;
      });
    }

    if (tariffDetails.isParkingFeeEnabled && tariffDetails.parkingFeeDetails) {
      const parking = tariffDetails.parkingFeeDetails;
      request.isParkingFeeEnabled = true;
      request.parkingFeeDetails = {
        price: Number(parking.price),
        unit: parking.unit,
        graceMinutes: Number(parking.graceMinutes),
        ...(parking.minChargingDurationMinutes !== '' &&
        parking.minChargingDurationMinutes !== undefined
          ? { minChargingDurationMinutes: Number(parking.minChargingDurationMinutes) }
          : {}),
        billingStepSeconds: Number(parking.billingStepMinutes) * 60,
        ...(parking.maxCap ? { maxCap: Number(parking.maxCap) } : {}),
      };
    }

    if (tariffDetails.isReservationFeeEnabled && tariffDetails.reservationFeeDetails) {
      const reservation = tariffDetails.reservationFeeDetails;
      request.isReservationFeeEnabled = true;
      request.reservationFeeDetails = {
        reservationPrice: {
          priceType: reservation.reservationPrice.priceType,
          value: Number(reservation.reservationPrice.value || 0),
        },
        graceMinutes: Number(reservation.graceMinutes),
        maxHoldMinutes: Number(reservation.maxHoldMinutes),
        noShowCharge: {
          chargeType: reservation.noShowCharge.chargeType,
          value: Number(reservation.noShowCharge.value || 0),
        },
        feeOnSuccess: {
          actionType: reservation.feeOnSuccess.actionType,
          value: 0,
        },
        expiryGraceMinutes: Number(reservation.expiryGraceMinutes),
      };
    }

    if (
      tariffDetails.taxCountry?.scopeId &&
      tariffDetails.taxComponents &&
      tariffDetails.taxComponents.length > 0
    ) {
      request.taxCountry = {
        scopeId: tariffDetails.taxCountry.scopeId,
        scopeName: tariffDetails.taxCountry.scopeName,
      };
      request.taxComponents = tariffDetails.taxComponents.map((comp) => ({
        name: comp.name,
        percentage: Number(comp.percentage),
      }));
    }

    setLoading(true);
    tariffApi
      .createTariff(request)
      .then(() => {
        setLoading(false);
        setOpenStatus(true);
      })
      .catch((error: any) => {
        setLoading(false);
        const errorMessage = error?.response?.data?.message || t('failedToCreateTariff');
        dispatch(
          setNotificationMessage({
            status: true,
            message: errorMessage,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
        const lowerMessage = errorMessage.toLowerCase();
        if (
          lowerMessage.includes('deactivat') ||
          lowerMessage.includes('status') ||
          lowerMessage.includes('inactive') ||
          lowerMessage.includes('active')
        ) {
          setErrors((previous) => ({ ...previous, status: errorMessage }));
        }
      });
  };

  useEffect(() => {
    if (connectorRatingsAll.length === 0) {
      dispatch(fetchAllConnectorRating());
    }
    if (!(scopeList as { country?: unknown[] })?.country?.length) {
      dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
    }
  }, [dispatch]);

  return (
    <div className="add-tariff-wrapper">
      <ModalBox
        open={openStatus}
        title=""
        content={
          <SuccessNotification
            title={t('chargingTariffCreatedSuccessfully')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              if (tariffDetails.tariffType === 'global') {
                dispatch(fetchAllTariffs({ page: 1, limit: 20, type: 'global' }));
                dispatch(fetchTariffCounts('global'));
              }
              if (tariffDetails.tariffType === 'custom') {
                dispatch(fetchAllCustomTariffs({ page: 1, limit: 20, type: 'custom' }));
                dispatch(fetchCustomTariffCounts('custom'));
              }
              navigate('/ev-admin/tariffs');
            }}
          />
        }
        closeModal={() => setOpenStatus(false)}
        header={false}
        rightCorner=""
        notification
      />
      <PageTitle
        title={t('addChargingTariffs')}
        topRightContent={
          <div className="page-title-button-group">
            <MyButton
              label={t('cancel')}
              buttonType="outline"
              onClickFunc={() => navigate('/ev-admin/tariffs')}
            />

            <MyButton
              label={t('save')}
              buttonType="submit"
              disableButton={loading}
              loading={loading}
              onClickFunc={handleSave}
            />
          </div>
        }
      />

      <div className="add-tariff-form-card">
        <div className="form-section">
          <p className="section-title">{t('basicDetails')}</p>
          <div className="basic-details-grid">
            <CustomInput
              inputLabel={t('tariffName')}
              placeholder={t('enterTariffName')}
              name="tariffName"
              type="text"
              value={tariffDetails.tariffName}
              onChangeValue={(name, value) => updateTariffDetail('tariffName', value)}
              valueRequired
              error={!!errors.tariffName}
              errorMessage={errors.tariffName}
            />
            <SelectField
              inputLabel={t('currencyCode')}
              options={currencyCodesList}
              name="currency"
              selectedValue={tariffDetails.currency}
              placeholder={t('chooseCurrencyCode')}
              onChangeValue={(name, value) => updateTariffDetail('currency', value)}
              valueRequired
              error={!!errors.currency}
              errorMessage={errors.currency}
            />
            <SelectField
              inputLabel={t('timeZone')}
              options={timezones}
              name="timezone"
              selectedValue={tariffDetails.timezone}
              placeholder={t('chooseTimeZone')}
              onChangeValue={(name, value) => updateTariffDetail('timezone', value)}
              valueRequired
              error={!!errors.timezone}
              errorMessage={errors.timezone}
            />
            <SelectField
              inputLabel={t('tariffType')}
              options={[
                { label: t('custom'), value: 'custom' },
                { label: t('global'), value: 'global' },
              ]}
              name="tariffType"
              selectedValue={tariffDetails.tariffType}
              placeholder={t('chooseTariffType')}
              onChangeValue={(name, value) => updateTariffDetail('tariffType', value)}
              valueRequired
              error={!!errors.tariffType}
              errorMessage={errors.tariffType}
            />
            {tariffDetails.tariffType === 'custom' && (
              <SelectField
                inputLabel={t('scopeLevel')}
                options={scopeLeveelOptions}
                name="scope"
                selectedValue={(tariffDetails as any).scope || ''}
                placeholder={t('chooseScopeLevel')}
                onChangeValue={(name, value) => updateTariffDetail('scope', value)}
                valueRequired
                error={!!errors.scope}
                errorMessage={errors.scope}
              />
            )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope &&
              portalType !== PortalType.PARTNER &&
              tariffDetails.scope !== 'geography' && (
                <SelectField
                  inputLabel={t('partner')}
                  options={
                    accountList?.map((account: { accountName: any; accountCustomId: any }) => ({
                      label: account.accountName,
                      value: account.accountCustomId,
                    })) || []
                  }
                  name="partner"
                  selectedValue={tariffDetails.partner || ''}
                  onChangeValue={(name, value) => updateTariffDetail('partner', value)}
                  placeholder={t('choosePartner')}
                  valueRequired
                  error={!!errors.partner}
                  errorMessage={errors.partner}
                />
              )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope &&
              tariffDetails.scope === 'station' && (
                <SelectField
                  inputLabel={t('station')}
                  options={stationsList || []}
                  name="station"
                  selectedValue={tariffDetails.station || ''}
                  onChangeValue={(name, value) => updateTariffDetail('station', value)}
                  placeholder={t('chooseStation')}
                  valueRequired
                  error={!!errors.station}
                  errorMessage={errors.station}
                />
              )}
            {tariffDetails.tariffType === 'custom' && tariffDetails.scope === 'geography' && (
              <SelectField
                inputLabel={t('country')}
                options={
                  (scopeList as { country?: Array<{ label: string; value: string }> })?.country ||
                  []
                }
                name="country"
                selectedValue={(tariffDetails as any).country || ''}
                onChangeValue={(name, value) => updateTariffDetail('country', value)}
                placeholder={t('chooseCountry')}
                valueRequired
                error={!!errors.country}
                errorMessage={errors.country}
              />
            )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope === 'geography' &&
              tariffDetails.country && (
                <SelectField
                  inputLabel={
                    tariffDetails.stateChildType === 'State'
                      ? t('state')
                      : tariffDetails.stateChildType || t('state')
                  }
                  options={
                    (scopeList as { state?: Array<{ label: string; value: string }> })?.state || []
                  }
                  name="state"
                  selectedValue={(tariffDetails as any).state || ''}
                  onChangeValue={(name, value) => updateTariffDetail('state', value)}
                  placeholder={t('chooseState')}
                />
              )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope === 'geography' &&
              tariffDetails.country &&
              tariffDetails.state && (
                <SelectField
                  inputLabel={
                    tariffDetails.cityChildType === 'City'
                      ? t('city')
                      : tariffDetails.cityChildType || t('city')
                  }
                  options={
                    (scopeList as { city?: Array<{ label: string; value: string }> })?.city || []
                  }
                  name="city"
                  selectedValue={(tariffDetails as any).city || ''}
                  onChangeValue={(name, value) => updateTariffDetail('city', value)}
                  placeholder={t('chooseCity')}
                />
              )}
            {/* {tariffDetails.tariffType === 'custom' && tariffDetails.scope === 'geography' && ( */}
            {tariffDetails.tariffType === 'custom' && (
              <CustomInput
                inputLabel={t('scopeId')}
                placeholder={t('scopeIdAutoGenerated')}
                name="scopeId"
                type="text"
                readOnly
                value={tariffDetails.scopeId || ''}
                onChangeValue={(name, value) => updateTariffDetail('scopeId', value)}
              />
            )}
            {tariffDetails.tariffType === 'custom' && (
              <CustomInput
                inputLabel={t('scopeName')}
                placeholder={t('scopeName')}
                name="scopeName"
                value={tariffDetails.scopeName || ''}
                type="text"
                onChangeValue={(name, value) => updateTariffDetail('scopeName', value)}
              />
            )}
          </div>
        </div>

        <div className="form-section">
          <p className="section-title">{t('technicalDetails')}</p>

          <div className="technical-details-grid">
            <div className="select-field-width">
              <SelectField
                inputLabel={t('currentType')}
                options={[
                  {
                    label: 'AC',
                    value: 'AC',
                  },
                  {
                    label: 'DC',
                    value: 'DC',
                  },
                ]}
                placeholder={t('chooseCurrentType')}
                selectedValue={tariffDetails.currentType}
                name="currentType"
                onChangeValue={(name, value) => updateTariffDetail('currentType', value)}
                valueRequired
                error={!!errors.currentType}
                errorMessage={errors.currentType}
              />
            </div>
            <div className="select-field-width">
              <SelectField
                inputLabel={t('connectorRatingKw')}
                options={connectorRatingsAll
                  .filter((rating: any) => rating.currentType === tariffDetails.currentType)
                  .map((rating: any) => ({
                    label: `${rating.currentType} ${rating.capacityKW} KW`,
                    value: rating._id,
                  }))}
                placeholder={t('chooseConnectorRating')}
                selectedValue={tariffDetails.connectorRating}
                name="connectorRating"
                onChangeValue={(name, value) => updateTariffDetail('connectorRating', value)}
                valueRequired
                error={!!errors.connectorRating}
                errorMessage={errors.connectorRating}
              />
            </div>

            <CustomDatePicker
              inputLabel={t('enforcementDate')}
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="enforcementDate"
              value={
                tariffDetails.enforcementDate?.includes(':')
                  ? tariffDetails.enforcementDate
                  : tariffDetails.enforcementDate?.split(' ')[0]
              }
              onChangeValue={(name, value) => updateTariffDetail('enforcementDate', value)}
              valueRequired
              error={!!errors.enforcementDate}
              errorMessage={errors.enforcementDate}
              minDate={changeDateFormat(new Date().toISOString())}
            />
            <CustomDatePicker
              inputLabel={t('expiryDateOptional')}
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="expiryDate"
              value={
                tariffDetails.expiryDate?.includes(':')
                  ? tariffDetails.expiryDate
                  : tariffDetails.expiryDate?.split(' ')[0]
              }
              onChangeValue={(name, value) => updateTariffDetail('expiryDate', value)}
              error={!!errors.expiryDate}
              errorMessage={errors.expiryDate}
              minDate={
                tariffDetails.enforcementDate
                  ? tariffDetails.enforcementDate.split(' ')[0]
                  : changeDateFormat(new Date().toISOString())
              }
            />
          </div>
          <div className="status-section">
            <p className="status-label">{t('status')}</p>
            <div className="status-radio-group">
              <CustomRadioButton
                name="status"
                label={t('active')}
                value="Active"
                selected={tariffDetails.status === true}
                onChangeFunc={(name, value) => updateTariffDetail('status', value === 'Active')}
              />
              <CustomRadioButton
                name="status"
                label={t('inactive')}
                value="Inactive"
                selected={tariffDetails.status === false}
                onChangeFunc={(name, value) => updateTariffDetail('status', value === 'Active')}
              />
            </div>
            {errors.status && (
              <p
                className="custom-input-error-message"
                style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {errors.status}
              </p>
            )}
          </div>
        </div>

        <div className="form-section price-components-section">
          <div className="section-header-flex">
            <p className="section-title">{t('priceComponents')}</p>
            <MyButton
              label={t('addComponents')}
              buttonType="primary-outline"
              svgIcon={<Plus />}
              onClickFunc={addPriceComponent}
            />
          </div>
          {errors.priceComponents && (
            <p
              className="error-message"
              style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {errors.priceComponents}
            </p>
          )}

          <div className="price-components-list">
            {tariffDetails.priceComponents.map((comp) => (
              <div key={comp.id} className="price-component-grid">
                <SelectField
                  inputLabel={t('componentType')}
                  options={[
                    { label: t('basePrice'), value: 'Base price' },
                    { label: t('operatingCost'), value: 'Operational cost' },
                  ]}
                  name={`type-${comp.id}`}
                  placeholder={t('selectComponentType')}
                  selectedValue={comp.type}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'type', value)}
                />
                <CustomInput
                  inputLabel={t('componentName')}
                  placeholder={t('enterComponentName')}
                  name={`name-${comp.id}`}
                  type="text"
                  value={comp.name}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'name', value)}
                  valueRequired
                  error={!!priceComponentErrors[comp.id]?.name}
                  errorMessage={priceComponentErrors[comp.id]?.name}
                />
                <SelectField
                  inputLabel={t('valueType')}
                  options={[
                    { label: t('perKwh'), value: 'per-kWh' },
                    { label: t('perPercentage'), value: 'percentage' },
                  ]}
                  name={`valueType-${comp.id}`}
                  placeholder={t('selectValueType')}
                  selectedValue={comp.valueType}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'valueType', value)}
                />
                <CustomInput
                  inputLabel={
                    tariffDetails.currency
                      ? `${t('value')} (${tariffDetails.currency})`
                      : t('value')
                  }
                  placeholder={t('enterValue')}
                  suffixText={tariffDetails.currency || ''}
                  name={`value-${comp.id}`}
                  type="number"
                  value={comp.value}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'value', value)}
                  valueRequired
                  maxValue={1000}
                  error={!!priceComponentErrors[comp.id]?.value}
                  errorMessage={priceComponentErrors[comp.id]?.value}
                  decimalLimit={2}
                />
                <CustomInput
                  inputLabel={t('order')}
                  placeholder={t('order')}
                  name={`order-${comp.id}`}
                  type="number"
                  value={comp.order}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'order', value)}
                  maxValue={1000}
                  decimalLimit={0}
                />
                {canDeletePriceComponent(comp) && (
                  <div
                    className="delete-action-button"
                    role="presentation"
                    onClick={() => removePriceComponent(comp.id)}>
                    <DeleteIcon width="16" height="16" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {timeSlot && (
          <div className="form-section time-based-pricing-section">
            <div className="section-header-flex">
              <p className="section-title">{t('timeBasedPricingSlots')}</p>
              <MyButton
                label={t('addTimeSlot')}
                buttonType="primary-outline"
                svgIcon={<Plus />}
                onClickFunc={addTimeSlot}
              />
            </div>

            {tariffDetails.timeBasedPricingSlots &&
              tariffDetails.timeBasedPricingSlots.length > 0 && (
                <div className="time-slots-list">
                  {/* <div className="time-slot-header-row">
                  <span>Slot Name</span>
                  <span>Start Time</span>
                  <span>End Time</span>
                  <span>Recurrence Type</span>
                  <span>Date</span>
                  <span>Days</span>
                  <span>Multiplier</span>
                  <span />
                </div> */}
                  {tariffDetails.timeBasedPricingSlots.map((slot) => (
                    <div key={slot.id} className="time-slot-grid">
                      <CustomInput
                        inputLabel={t('slotName')}
                        name={`slotName-${slot.id}`}
                        type="text"
                        value={slot.name}
                        placeholder={t('enterSlotName')}
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'name', String(value))
                        }
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.name}
                        errorMessage={timeSlotErrors[slot.id]?.name}
                      />
                      <CustomTimePicker
                        inputLabel={t('startTime')}
                        name={`startTime-${slot.id}`}
                        value={slot.startTime}
                        placeholder="00:00"
                        onChangeValue={(name, value) => updateTimeSlot(slot.id, 'startTime', value)}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.startTime}
                        errorMessage={timeSlotErrors[slot.id]?.startTime}
                      />
                      <CustomTimePicker
                        inputLabel={t('endTime')}
                        name={`endTime-${slot.id}`}
                        value={slot.endTime}
                        placeholder="00:00"
                        onChangeValue={(name, value) => updateTimeSlot(slot.id, 'endTime', value)}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.endTime}
                        errorMessage={timeSlotErrors[slot.id]?.endTime}
                      />
                      <SelectField
                        inputLabel={t('recurrenceType')}
                        options={[
                          { label: t('daily'), value: 'daily' },
                          { label: t('weekly'), value: 'weekly' },
                          { label: t('nonRecurring'), value: 'non-recurring' },
                        ]}
                        name={`recurrenceType-${slot.id}`}
                        placeholder={t('selectRecurrenceType')}
                        selectedValue={slot.recurrenceType}
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'recurrenceType', value)
                        }
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.recurrenceType}
                        errorMessage={timeSlotErrors[slot.id]?.recurrenceType}
                      />

                      <CustomInput
                        inputLabel={t('priceMultiplier')}
                        name={`multiplier-${slot.id}`}
                        type="number"
                        value={slot.priceMultiplier}
                        placeholder="1.0"
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'priceMultiplier', String(value))
                        }
                        decimalLimit={2}
                        maxValue={10}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.priceMultiplier}
                        errorMessage={timeSlotErrors[slot.id]?.priceMultiplier}
                      />
                      <div
                        className="delete-action-button"
                        role="presentation"
                        onClick={() => removeTimeSlot(slot.id)}>
                        <DeleteIcon width="16" height="16" />
                      </div>
                      {slot.recurrenceType === 'non-recurring' && (
                        <div className="time-slot-conditional-row non-recurring-row">
                          <CustomMultiDatePicker
                            inputLabel={t('date')}
                            name={`date-${slot.id}`}
                            value={slot.dates || []}
                            placeholder="DD/MM/YYYY"
                            validFromDate={tariffDetails.enforcementDate?.split(' ')[0] || ''}
                            validToDate={tariffDetails.expiryDate?.split(' ')[0] || ''}
                            onChangeValue={(name, value) => updateTimeSlot(slot.id, 'dates', value)}
                            valueRequired
                            error={!!timeSlotErrors[slot.id]?.dates}
                            errorMessage={timeSlotErrors[slot.id]?.dates}
                          />
                        </div>
                      )}
                      {slot.recurrenceType === 'weekly' && (
                        <div className="time-slot-conditional-row weekly-row">
                          <CheckBoxButtons
                            inputLabel={t('daysOfWeek')}
                            name={`daysOfWeek-${slot.id}`}
                            list={daysOfWeekOptions}
                            value={slot.daysOfWeek}
                            onChangeValue={(name, values) =>
                              updateTimeSlot(slot.id, 'daysOfWeek', values as string[])
                            }
                            valueRequired
                            error={!!timeSlotErrors[slot.id]?.daysOfWeek}
                            errorMessage={timeSlotErrors[slot.id]?.daysOfWeek}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* Parking fee, reservation fee, and tax sections temporarily disabled in the UI
        <div className="form-section parking-fee-section">
          <div className="section-header-flex">
            <p className="section-title">{t('parkingFee')}</p>
            <SwitchButton
              checked={!!tariffDetails.isParkingFeeEnabled}
              change={(checked) => updateTariffDetail('isParkingFeeEnabled', checked)}
            />
          </div>
          {tariffDetails.isParkingFeeEnabled && (
            <div className="fee-details-grid">
              <CustomInput
                inputLabel={
                  tariffDetails.currency
                    ? `${t('price')} (${tariffDetails.currency} / ${t('minutes')})`
                    : `${t('price')} (${t('minutes')})`
                }
                placeholder={t('enterPrice')}
                suffixText={tariffDetails.currency || ''}
                name="parkingPrice"
                type="number"
                value={tariffDetails.parkingFeeDetails?.price ?? ''}
                onChangeValue={(name, value) => updateParkingFeeDetail('price', String(value))}
                valueRequired
                decimalLimit={2}
                error={!!parkingFeeErrors.price}
                errorMessage={parkingFeeErrors.price}
              />
              <CustomInput
                inputLabel={t('gracePeriodMinutes')}
                placeholder={t('enterGracePeriod')}
                name="parkingGraceMinutes"
                type="number"
                value={tariffDetails.parkingFeeDetails?.graceMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateParkingFeeDetail('graceMinutes', String(value))
                }
                valueRequired
                decimalLimit={0}
                error={!!parkingFeeErrors.graceMinutes}
                errorMessage={parkingFeeErrors.graceMinutes}
              />
              <CustomInput
                inputLabel={t('minChargingDurationMinutes')}
                placeholder={t('enterMinChargingDuration')}
                name="parkingMinChargingDurationMinutes"
                type="number"
                value={tariffDetails.parkingFeeDetails?.minChargingDurationMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateParkingFeeDetail('minChargingDurationMinutes', String(value))
                }
                decimalLimit={0}
                error={!!parkingFeeErrors.minChargingDurationMinutes}
                errorMessage={parkingFeeErrors.minChargingDurationMinutes}
              />
              <CustomInput
                inputLabel={t('billingStepMinutes')}
                placeholder={t('enterBillingStep')}
                name="parkingBillingStepMinutes"
                type="number"
                value={tariffDetails.parkingFeeDetails?.billingStepMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateParkingFeeDetail('billingStepMinutes', String(value))
                }
                valueRequired
                decimalLimit={0}
                error={!!parkingFeeErrors.billingStepMinutes}
                errorMessage={parkingFeeErrors.billingStepMinutes}
              />
              <CustomInput
                inputLabel={t('maxCapOptional')}
                placeholder={t('enterMaxCap')}
                suffixText={tariffDetails.currency || ''}
                name="parkingMaxCap"
                type="number"
                value={tariffDetails.parkingFeeDetails?.maxCap ?? ''}
                onChangeValue={(name, value) => updateParkingFeeDetail('maxCap', String(value))}
                decimalLimit={2}
                error={!!parkingFeeErrors.maxCap}
                errorMessage={parkingFeeErrors.maxCap}
              />
            </div>
          )}
        </div>

        <div className="form-section reservation-fee-section">
          <div className="section-header-flex">
            <p className="section-title">{t('reservationFee')}</p>
            <SwitchButton
              checked={!!tariffDetails.isReservationFeeEnabled}
              change={(checked) => updateTariffDetail('isReservationFeeEnabled', checked)}
            />
          </div>
          {tariffDetails.isReservationFeeEnabled && (
            <div className="fee-details-grid">
              <SelectField
                inputLabel={t('reservationPriceType')}
                options={[
                  { label: t('perMinute'), value: 'per-minute' },
                  { label: t('flat'), value: 'flat' },
                  { label: t('zero'), value: 'zero' },
                ]}
                name="reservationPriceType"
                selectedValue={
                  tariffDetails.reservationFeeDetails?.reservationPrice.priceType || 'per-minute'
                }
                placeholder={t('choosePriceType')}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('reservationPrice.priceType', value)
                }
                valueRequired
                error={!!reservationFeeErrors['reservationPrice.priceType']}
                errorMessage={reservationFeeErrors['reservationPrice.priceType']}
              />
              <CustomInput
                inputLabel={
                  tariffDetails.currency
                    ? `${t('reservationPriceValue')} (${tariffDetails.currency})`
                    : t('reservationPriceValue')
                }
                placeholder={t('enterValue')}
                suffixText={tariffDetails.currency || ''}
                name="reservationPriceValue"
                type="number"
                value={tariffDetails.reservationFeeDetails?.reservationPrice.value ?? ''}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('reservationPrice.value', String(value))
                }
                disabled={
                  tariffDetails.reservationFeeDetails?.reservationPrice.priceType === 'zero'
                }
                decimalLimit={2}
                error={!!reservationFeeErrors['reservationPrice.value']}
                errorMessage={reservationFeeErrors['reservationPrice.value']}
              />
              <CustomInput
                inputLabel={t('gracePeriodMinutes')}
                placeholder={t('enterGracePeriod')}
                name="reservationGraceMinutes"
                type="number"
                value={tariffDetails.reservationFeeDetails?.graceMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('graceMinutes', String(value))
                }
                valueRequired
                decimalLimit={0}
                error={!!reservationFeeErrors.graceMinutes}
                errorMessage={reservationFeeErrors.graceMinutes}
              />
              <CustomInput
                inputLabel={t('maxHoldMinutes')}
                placeholder={t('enterMaxHoldMinutes')}
                name="maxHoldMinutes"
                type="number"
                value={tariffDetails.reservationFeeDetails?.maxHoldMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('maxHoldMinutes', String(value))
                }
                valueRequired
                decimalLimit={0}
                error={!!reservationFeeErrors.maxHoldMinutes}
                errorMessage={reservationFeeErrors.maxHoldMinutes}
              />
              <SelectField
                inputLabel={t('noShowChargeType')}
                options={[
                  { label: t('heldTime'), value: 'held-time' },
                  { label: t('flat'), value: 'flat' },
                  { label: t('none'), value: 'none' },
                ]}
                name="noShowChargeType"
                selectedValue={
                  tariffDetails.reservationFeeDetails?.noShowCharge.chargeType || 'held-time'
                }
                placeholder={t('chooseChargeType')}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('noShowCharge.chargeType', value)
                }
                valueRequired
                error={!!reservationFeeErrors['noShowCharge.chargeType']}
                errorMessage={reservationFeeErrors['noShowCharge.chargeType']}
              />
              <CustomInput
                inputLabel={
                  tariffDetails.currency
                    ? `${t('noShowChargeValue')} (${tariffDetails.currency})`
                    : t('noShowChargeValue')
                }
                placeholder={t('enterValue')}
                suffixText={tariffDetails.currency || ''}
                name="noShowChargeValue"
                type="number"
                value={tariffDetails.reservationFeeDetails?.noShowCharge.value ?? ''}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('noShowCharge.value', String(value))
                }
                disabled={tariffDetails.reservationFeeDetails?.noShowCharge.chargeType !== 'flat'}
                decimalLimit={2}
                error={!!reservationFeeErrors['noShowCharge.value']}
                errorMessage={reservationFeeErrors['noShowCharge.value']}
              />
              <SelectField
                inputLabel={t('feeOnSuccessAction')}
                options={[
                  { label: t('waive'), value: 'waive' },
                  { label: t('credit'), value: 'credit' },
                  { label: t('add'), value: 'add' },
                ]}
                name="feeOnSuccessAction"
                selectedValue={
                  tariffDetails.reservationFeeDetails?.feeOnSuccess.actionType || 'waive'
                }
                placeholder={t('chooseAction')}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('feeOnSuccess.actionType', value)
                }
                valueRequired
                error={!!reservationFeeErrors['feeOnSuccess.actionType']}
                errorMessage={reservationFeeErrors['feeOnSuccess.actionType']}
              />
              <CustomInput
                inputLabel={t('expiryGraceMinutes')}
                placeholder={t('enterExpiryGrace')}
                name="expiryGraceMinutes"
                type="number"
                value={tariffDetails.reservationFeeDetails?.expiryGraceMinutes ?? ''}
                onChangeValue={(name, value) =>
                  updateReservationFeeDetail('expiryGraceMinutes', String(value))
                }
                valueRequired
                decimalLimit={0}
                error={!!reservationFeeErrors.expiryGraceMinutes}
                errorMessage={reservationFeeErrors.expiryGraceMinutes}
              />
            </div>
          )}
        </div>

        <div className="form-section tax-section">
          <div className="section-header-flex">
            <p className="section-title">{t('taxSectionTitle')}</p>
          </div>
          <div className="fee-details-grid">
            <SelectField
              inputLabel={t('taxCountry')}
              options={(scopeList as { country?: Array<{ label: string; value: string }> })?.country || []}
              name="taxCountry"
              selectedValue={tariffDetails.taxCountry?.scopeId || ''}
              placeholder={t('chooseTaxCountry')}
              onChangeValue={(name, value) => updateTaxCountry(String(value))}
              error={!!errors.taxCountry}
              errorMessage={errors.taxCountry}
            />
          </div>

          <div className="section-header-flex tax-components-header">
            <p className="section-title">{t('taxComponents')}</p>
            <MyButton
              label={t('addTaxComponent')}
              buttonType="primary-outline"
              svgIcon={<Plus />}
              onClickFunc={addTaxComponent}
            />
          </div>
          {tariffDetails.taxComponents && tariffDetails.taxComponents.length > 0 && (
            <div className="tax-components-list">
              {tariffDetails.taxComponents.map((comp) => (
                <div key={comp.id} className="tax-component-grid">
                  <CustomInput
                    inputLabel={t('taxComponentName')}
                    placeholder={t('enterTaxComponentName')}
                    name={`taxComponentName-${comp.id}`}
                    type="text"
                    value={comp.name}
                    onChangeValue={(name, value) => updateTaxComponent(comp.id, 'name', String(value))}
                    valueRequired
                    error={!!taxComponentErrors[comp.id]?.name}
                    errorMessage={taxComponentErrors[comp.id]?.name}
                  />
                  <CustomInput
                    inputLabel={t('taxPercentage')}
                    placeholder={t('enterTaxPercentage')}
                    name={`taxPercentage-${comp.id}`}
                    type="number"
                    value={comp.percentage}
                    onChangeValue={(name, value) => updateTaxComponent(comp.id, 'percentage', String(value))}
                    valueRequired
                    decimalLimit={2}
                    error={!!taxComponentErrors[comp.id]?.percentage}
                    errorMessage={taxComponentErrors[comp.id]?.percentage}
                  />
                  <div
                    className="delete-action-button"
                    role="presentation"
                    onClick={() => removeTaxComponent(comp.id)}
                    title={t('delete') || 'Delete'}>
                    <DeleteIcon width="16" height="16" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        */}
      </div>
    </div>
  );
}

export default AddTariff;
